import React, { useEffect, useRef, useState } from "react";
import { ShipmentDetail, ShipmentStatus as S } from "../types";
import { shippingService } from "../services/dataService";
import { usePermissions } from "../context/PermissionContext";
import { toDateTimeInput, todayLocal } from "../utils/dates";
import { shErr, shFmtQty } from "../utils/shipping";
import "../pages/PurchaseOrders.css";

export type ShipmentAction = "ship" | "deliver" | "cancel";

interface Props {
  shipment: ShipmentDetail;
  action: ShipmentAction;
  onClose: () => void;
  onDone: (message: string) => void;
}

const nowLocal = () => { const d = new Date(); d.setSeconds(0, 0); return toDateTimeInput(d); };
const today = () => todayLocal();
const TITLE: Record<ShipmentAction, string> = { ship: "Sevk et", deliver: "Teslim edildi", cancel: "Sevkiyatı iptal et" };
const BUSY: Record<ShipmentAction, string> = { ship: "Sevk ediliyor", deliver: "Kaydediliyor", cancel: "İptal ediliyor" };
const HINT: Record<ShipmentAction, string> = {
  ship: "Sevk zamanı belge tarihi olur; resmi irsaliye numarası siparişlerin faturasında referans olarak kullanılır.",
  deliver: "Teslim alan kişi ve zaman kaydedilir; teslim edilmiş sevkiyat iptal edilemez.",
  cancel: "İptal edilen sevkiyat yeniden açılamaz. Sebep sevkiyat kartında görünür.",
};
const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
};

/** Sevkiyat işlemleri: Sevk et (irsaliye no + tarih), Teslim edildi (teslim alan + zaman), İptal (sebep). Tek modal, işlem türüne göre alanlar değişir. */
const ShipmentActionModal: React.FC<Props> = ({ shipment, action, onClose, onDone }) => {
  const { hasModule } = usePermissions();
  const [when, setWhen] = useState(nowLocal());
  const [waybillNo, setWaybillNo] = useState(shipment.waybillNo || "");
  const [waybillDate, setWaybillDate] = useState(shipment.waybillDate ? shipment.waybillDate.slice(0, 10) : today());
  const [receivedBy, setReceivedBy] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Açan öğe ilk render'da (odak modala geçmeden) yakalanır
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const firstRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const setFirst = (el: HTMLInputElement | HTMLTextAreaElement | null) => { firstRef.current = el; };

  const n = shipment.lines.length;
  const valid = action === "ship" ? true : action === "deliver" ? !!receivedBy.trim() : reason.trim().length >= 3;

  const run = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (saving) return;
    setError("");
    if (action === "cancel" && reason.trim().length < 3) return setError("İptal sebebi en az 3 karakter olmalı.");
    if (action === "deliver" && !receivedBy.trim()) return setError("Teslim alan kişiyi yazın.");
    if (action !== "cancel" && !when) return setError(action === "ship" ? "Sevk zamanını girin." : "Teslim zamanını girin.");
    setSaving(true);
    try {
      if (action === "ship") {
        const r = await shippingService.ship(shipment.shipmentId, {
          shippedAt: `${when}:00`, // yerel duvar saati, "Z"siz: sunucu TR saati tutar, toISOString 3 saat geri yazar
          waybillNo: waybillNo.trim() || null,
          waybillDate: waybillNo.trim() && waybillDate ? waybillDate : null, // tarih boşsa sunucu sevk tarihini yazar
        });
        onDone(r.stockMovements > 0 ? `Sevk edildi; ${r.stockMovements} stok çıkışı işlendi.` : "Sevk edildi.");
      } else if (action === "deliver") {
        await shippingService.deliver(shipment.shipmentId, { deliveredAt: `${when}:00`, receivedBy: receivedBy.trim() });
        onDone("Teslimat onaylandı.");
      } else {
        await shippingService.cancel(shipment.shipmentId, reason.trim());
        onDone("Sevkiyat iptal edildi.");
      }
      onClose();
    } catch (err: any) { setError(shErr(err, "İşlem yapılamadı.")); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) { e.stopPropagation(); onClose(); }
      else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); run(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, saving, when, waybillNo, waybillDate, receivedBy, reason]);

  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  // autoFocus yerine efekt: StrictMode geliştirmede yukarıdaki geri-odak temizliğini bir kez erken çalıştırır, autoFocus ezilirdi
  useEffect(() => { firstRef.current?.focus(); }, []);

  return (
    <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form className="po-modal" role="dialog" aria-modal="true" aria-labelledby="sh-act-title" onSubmit={run}>
        <div className="po-modal-head">
          <div><span className="po-kicker">{shipment.number} · {shipment.firmName}</span><h3 id="sh-act-title">{TITLE[action]}</h3></div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={saving}>{I.close}</button>
        </div>

        <div className="po-modal-body">
          <p className="po-form-hint">{HINT[action]}</p>
          {error && <div className="po-note err" role="alert">{error}</div>}

          {action === "ship" && <>
            <div className="po-grid2">
              <label className="po-field"><span>Sevk zamanı</span><input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></label>
              <label className="po-field"><span>Resmi irsaliye no<em>isteğe bağlı</em></span><input value={waybillNo} maxLength={32} ref={setFirst} placeholder="Kağıt / portal irsaliye no" onChange={(e) => setWaybillNo(e.target.value)} />
                {shipment.waybillNo && !waybillNo.trim() && <span className="po-fieldnote warn">Boş bırakılırsa kayıtlı no ({shipment.waybillNo}) korunur; silmek için taslağı düzenleyin.</span>}</label>
              <label className="po-field"><span>İrsaliye tarihi</span><input type="date" value={waybillDate} disabled={!waybillNo.trim()} onChange={(e) => setWaybillDate(e.target.value)} /></label>
            </div>
            <div className="po-note info">
              {hasModule("stock")
                ? `${n} satır sevk edilecek; üretim stoğu tanımlı modeller için stoktan çıkış işlenir.`
                : `${n} satır sevk edilecek. Stok modülü lisansta olmadığı için stok bakiyesi değişmez.`}
            </div>
          </>}

          {action === "deliver" && (
            <div className="po-grid2">
              <label className="po-field"><span>Teslim alan<em>zorunlu</em></span><input value={receivedBy} maxLength={100} ref={setFirst} placeholder="Ad soyad / kaşe" onChange={(e) => setReceivedBy(e.target.value)} /></label>
              <label className="po-field"><span>Teslim zamanı</span><input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></label>
            </div>
          )}

          {action === "cancel" && <>
            <label className="po-field">
              <span>İptal sebebi<em>zorunlu, en az 3 karakter</em></span>
              <textarea rows={3} maxLength={300} ref={setFirst} value={reason} placeholder="Örn. müşteri teslimatı erteledi" onChange={(e) => setReason(e.target.value)} />
            </label>
            <span className="po-counter">{reason.length}/300</span>
            <div className="po-note warn">
              {shipment.lines.some((l) => l.hasStockMovement)
                ? "Sevk ile üretilen stok çıkışları geri alınır; kayıt iptal olarak kalır."
                : shipment.status === S.Draft
                  ? "Taslak iptal olarak kalır; kalıcı olarak kaldırmak için Sil kullanın."
                  : "Kayıt iptal olarak kalır; sipariş miktarı yeniden sevk edilebilir."}
            </div>
          </>}
        </div>

        <div className="po-modal-foot between">
          <span className="po-hint"><b>{n}</b> satır · <b>{shFmtQty(shipment.totalQuantity)}</b> birim</span>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button type="submit" className={`po-btn primary${action === "cancel" ? " danger" : ""}`} disabled={saving || !valid}>{saving ? BUSY[action] : TITLE[action]}</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ShipmentActionModal;
